import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { normalizeDropdownSearch, shouldSearchDropdown } from './dropdownSearch';
import './animated-dropdown.css';

const cn = (...inputs: Parameters<typeof clsx>) => twMerge(clsx(...inputs));

export interface DropdownOption {
  value: string;
  label: ReactNode;
  searchText?: string;
}

export interface AnimatedDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  buttonClassName?: string;
  placeholder?: string;
  id?: string;
  prefix?: ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  variant?: 'admin' | 'storefront';
  onSaveShortcut?: () => void;
}

export function AnimatedDropdown({
  options, value, onChange, className, buttonClassName,
  placeholder = 'انتخاب کنید', id, prefix, ariaLabel, disabled = false, variant = 'admin', onSaveShortcut,
}: AnimatedDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const searchable = shouldSearchDropdown(options.length);
  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const needle = normalizeDropdownSearch(query);
    return options.filter((option) => normalizeDropdownSearch(
      `${option.searchText ?? (typeof option.label === 'string' ? option.label : '')} ${option.value}`,
    ).includes(needle));
  }, [options, query, searchable]);

  useEffect(() => { setActiveIndex(0); }, [query]);
  useEffect(() => { if (disabled) setIsOpen(false); }, [disabled]);
  useEffect(() => {
    if (!isOpen) return;
    const list = panelRef.current?.querySelector<HTMLElement>('.searchable-select__options');
    const option = list?.querySelector<HTMLElement>(`[data-dropdown-index="${activeIndex}"]`);
    if (!list || !option) return;
    // scrollIntoView also scrolls the document for a portal. That can move the
    // trigger outside the viewport as soon as the menu opens.
    const listRect = list.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();
    if (optionRect.top < listRect.top) list.scrollTop -= listRect.top - optionRect.top;
    else if (optionRect.bottom > listRect.bottom) list.scrollTop += optionRect.bottom - listRect.bottom;
  }, [activeIndex, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger?.isConnected) { setIsOpen(false); return; }
      const rect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width = Math.min(viewportWidth - 16, Math.max(rect.width, 240));
      const left = Math.max(8, Math.min(rect.right - width, viewportWidth - width - 8));
      const below = viewportHeight - rect.bottom - 10;
      const above = rect.top - 10;
      const wantedHeight = Math.min(320, options.length * 39 + (searchable ? 58 : 12));
      const openAbove = below < Math.min(wantedHeight, 180) && above > below;
      const availableHeight = Math.max(80, openAbove ? above : below);
      const maxHeight = Math.min(wantedHeight, availableHeight);
      setPanelStyle({ position: 'fixed', left, top: openAbove ? rect.top - maxHeight - 5 : rect.bottom + 5, width, maxHeight });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, options.length, searchable]);

  useEffect(() => {
    if (!isOpen) return;
    // The panel is portalled after the application root. On catalogue pages the
    // root can be tens of thousands of pixels tall, so a normal focus call may
    // scroll the document all the way to the portal before its fixed position
    // has been painted. Keep the viewport anchored to the dropdown trigger.
    if (searchable) searchRef.current?.focus({ preventScroll: true });
    const closeOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) setIsOpen(false);
    };
    const closeEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') { setIsOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [isOpen, searchable]);

  function selectOption(option: DropdownOption) {
    onChange(option.value);
    setIsOpen(false);
    setQuery('');
    triggerRef.current?.focus({ preventScroll: true });
  }

  function handleListKeys(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && onSaveShortcut) {
      event.preventDefault();
      onSaveShortcut();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(0, Math.min(filteredOptions.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))));
    } else if (event.key === 'Enter' && filteredOptions[activeIndex]) {
      event.preventDefault();
      selectOption(filteredOptions[activeIndex]);
    }
  }

  return (
    <div className={cn('searchable-select', `searchable-select--${variant}`, className)}>
      <button ref={triggerRef} id={id} type="button" disabled={disabled}
        className={cn(variant === 'admin' ? 'a-btn a-btn--secondary w-full justify-between' : 'searchable-select__storefront-button', buttonClassName)}
        aria-label={ariaLabel} aria-haspopup="listbox" aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen} onClick={() => { setQuery(''); setActiveIndex(0); setIsOpen((open) => !open); }}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && onSaveShortcut) { event.preventDefault(); onSaveShortcut(); }
          else if (event.key === 'ArrowDown' && !isOpen) { event.preventDefault(); setIsOpen(true); }
          else if (isOpen) handleListKeys(event);
        }}>
        <span className="searchable-select__value">
          {prefix && <span className="searchable-select__prefix">{prefix}</span>}
          <span className="searchable-select__label">{selectedOption ? selectedOption.label : placeholder}</span>
        </span>
        <ChevronDown className={cn('searchable-select__chevron', isOpen && 'searchable-select__chevron--open')} aria-hidden="true" />
      </button>
      {isOpen && createPortal(
        <div ref={panelRef}
          className={cn('searchable-select__panel', `searchable-select__panel--${variant}`)}
          style={panelStyle} dir="rtl" onKeyDown={handleListKeys}>
          {searchable && <div className="searchable-select__search-wrap">
            <Search size={16} aria-hidden="true" />
            <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)}
              placeholder="جستجو در گزینه‌ها..." aria-label="جستجو در گزینه‌ها" />
          </div>}
          <div id={listboxId} role="listbox" aria-label={ariaLabel || placeholder} className="searchable-select__options">
            {filteredOptions.length ? filteredOptions.map((option, index) => (
              <button key={`${option.value}-${index}`} data-dropdown-index={index} type="button" role="option" tabIndex={-1} aria-selected={value === option.value}
                className={cn('searchable-select__option', value === option.value && 'searchable-select__option--selected', index === activeIndex && 'searchable-select__option--active')}
                onMouseEnter={() => setActiveIndex(index)} onClick={() => selectOption(option)}>{option.label}</button>
            )) : <div className="searchable-select__empty">گزینه‌ای پیدا نشد</div>}
          </div>
        </div>, document.body,
      )}
    </div>
  );
}
